<template>
  <fts-dropdown-select
    append-to-body
    :placement="placement"
    prevent-overflow-alt-axis
    prevent-overflow-tether-off
    append-toggle-class="states-design-toggle"
    content-class="dropdown-menu dropdown-menu-w-sm dropdown-menu--sticky states-design-dropdown-menu"
    :reference-id="`fts-confirmation ${referenceId}`"
    overlay
    class="fts-states-design-dropdown"
    ref="dropdownSelect"
    @content:keydown-enter="optionPointerSelect"
    @content:keydown-up="optionPointerUp"
    @content:keydown-down="optionPointerDown"
    @selected="onSelect"
    @show="onShow"
    @hide="onHide"
  >
    <template #toggle>
      <template v-if="selectedState && selectedState.id">
        <div class="states-design-toggle-suggest-state" v-if="isSuggestState">
          <fts-state :state="selectedState" :states="states" />
        </div>
        <fts-state :state="selectedState" :states="states" v-else />
      </template>
      <div class="states-design-toggle-label" v-else>
        ({{blankOptionLabel}})
      </div>
    </template>

    <template #menu="{ select }">
      <div class="dropdown-filter">
        <div class="fts-form-control-filter">
          <input
            class="fts-form-control"
            v-model="formData.name"
            placeholder="Type to create or select for next state"
            ref="filterKeywordInput"
            @input="dispatchAutofill"
            @keyup.enter.prevent
            @keydown.up.prevent
            @keydown.down.prevent
          />
          <div class="search-icon">
            <fts-spinner :size="14" show v-if="isCreateOption && autofillLoading" />
            <font-awesome-icon :icon="['fas', 'search']" fixed-width v-else />
          </div>
        </div>
      </div>

      <template v-for="(x, index) in getOptions">
        <div class="dropdown-label" :key="`label-${index}`" v-if="x.type === 'label'">
          {{x.value}}
        </div>

        <div
          :key="`create-${index}`"
          class="dropdown-option"
          :class="{'dropdown-option--active': selectedIndex === index}"
          @mouseenter="selectedIndex=index"
          @click="select(x)"
          :ref="`dropdown-option-${index}`"
          v-else-if="x.type === 'create'"
        >
          <fts-state :state="x.value" />
          <div class="dropdown-option-kbd-hint" v-if="x.type === 'create' && selectedIndex === index">
            <kbd>Enter</kbd> to create
          </div>
        </div>

        <div
          :key="`suggestion-${index}`"
          class="dropdown-option"
          :class="{'dropdown-option--active': selectedIndex === index}"
          @mouseenter="selectedIndex=index"
          @click="select(x)"
          :ref="`dropdown-option-${index}`"
          v-else-if="x.type === 'suggestion'"
        >
          <fts-state :state="x.value" />
        </div>

        <template v-else-if="x.type === 'clear'">
          <div :key="`divider-${index}`" class="dropdown-divider"></div>
          <div
            :key="`clear-${index}`"
            class="dropdown-option fts-text-default"
            :class="{'dropdown-option--active': selectedIndex === index}"
            @mouseenter="selectedIndex=index"
            @click="select(x)"
            :ref="`dropdown-option-${index}`"
          >
            {{blankOptionLabel}}
          </div>
        </template>

        <div
          :key="`option-${index}`"
          class="dropdown-option"
          :class="{'dropdown-option--active': selectedIndex === index}"
          @mouseenter="selectedIndex=index"
          @click="$event => selectState($event, x, select)"
          :ref="`dropdown-option-${index}`"
          v-else
        >
          <fts-state :state="x.value" />
          <div class="dropdown-option-action" v-if="designMode">
            <fts-assign-design-dropdown
              :assignees="x.value.assignees"
              :users="users"
              :toggle-max-length="3"
              :reference-for="referenceId"
              class="stopPropagation"
              @update="(val) => $emit('state-assignee:update', {...val, stateId: x.value.id})"
              v-if="!disableAssignDesign"
            />
            <fts-state-design-dropdown
              :state="x.value"
              :states="states"
              :reference-for="referenceId"
              placement="bottom-end"
              class="stopPropagation"
              @state:update="(val) => $emit('state:update', val)"
              @state:delete="$emit('state:delete', x.value.id)"
            >
              <div class="dropdown-option-state-manage-toggle">
                <font-awesome-icon :icon="['fas', 'ellipsis-v']" />
              </div>
            </fts-state-design-dropdown>
          </div>
        </div>
      </template>
    </template>
  </fts-dropdown-select>
</template>

<script>
import Vue from 'vue';
import Vuelidate from 'vuelidate';
Vue.use(Vuelidate);

import canvasDesign from '../commons/canvasDesign';
import { store as canvasDesignStore } from '../store/canvasDesignStore';
import commons from '../commons/commons';
import dropdown from '../commons/dropdown';
import validator from '../commons/designValidator';

import FTSAssignDesignDropdown from './FTSAssignDesignDropdown';
import FTSDropdownSelect from '../elements/FTSDropdownSelect';
import FTSSpinner from '../elements/FTSSpinner';
import FTSState from '../elements/FTSState';
import FTSStateDesignDropdown from './FTSStateDesignDropdown';

export default {
  components: {
    'fts-assign-design-dropdown': FTSAssignDesignDropdown,
    'fts-dropdown-select': FTSDropdownSelect,
    'fts-spinner': FTSSpinner,
    'fts-state': FTSState,
    'fts-state-design-dropdown': FTSStateDesignDropdown
  },
  props: {
    placement: String,
    states: Array,
    selectedState: Object,
    blankOptionLabel: {
      type: String,
      default: 'none'
    },
    initialSetSuggestState: Boolean,
    disableBlankOption: Boolean,
    disableAssignDesign: Boolean,
    designMode: Boolean
  },
  data: () => ({
    formData: {},
    selectedIndex: 0,
    autofillLoading: false
  }),
  validations() {
    let formData = {};
    formData = validator.stateDesign([...this.states, ...this.suggestions]);
    return { formData };
  },
  methods: {
    onShow() {
      this.$nextTick(() => {
        this.$refs.filterKeywordInput.focus();
      });
    },
    onHide() {
      Object.assign(this.$data, this.$options.data.apply(this));
    },
    onSelect(item) {
      if (item) {
        if (item.type === 'create' || item.type === 'suggestion') {
          this.$emit('option:create', item.value);
        } else if (item.type === 'select') {
          this.$emit('option:select', item.value)
        } else if (item.type === 'clear') {
          this.$emit('option:clear');
        }
      }
    },
    optionPointerSelect() {
      if (this.selectedIndex > -1 && this.getOptions[this.selectedIndex]) {
        this.$refs.dropdownSelect.select(this.getOptions[this.selectedIndex]);
      }
    },
    optionPointerUp(item) {
      this.selectedIndex = dropdown.optionPointerUp({
        currentIndex: this.selectedIndex,
        options: this.getOptions,
        exclude: (x) => x && x.type !== 'label',
        scrollEl: item.popperContentEl,
        refs: this.$refs
      });
    },
    optionPointerDown(item) {
      this.selectedIndex = dropdown.optionPointerDown({
        currentIndex: this.selectedIndex,
        options: this.getOptions,
        exclude: (x) => x && x.type !== 'label',
        scrollEl: item.popperContentEl,
        refs: this.$refs
      });
    },
    debounceInput: _.debounce(async vm => {
      if (vm.formData && vm.formData.name) {
        try {
          const res = await canvasDesignStore.dispatch("ai", {
            type: "state",
            text: vm.formData.name
          });
          vm.$set(vm.formData, "color", res.data);
        } catch (err) {
          console.error(err);
        } finally {
          vm.autofillLoading = false;
        }
      } else {
        vm.autofillLoading = false;
      }
    }, 800),
    dispatchAutofill() {
      if (this.designMode) {
        if (!this.autofillLoading) {
          this.autofillLoading = true;
        }
        this.debounceInput(this);
      }
    },
    selectState(event, item, select) {
      if (commons.elementStopPropagation(event)) {
        return;
      }
      select(item);
    }
  },
  computed: {
    users() {
      return canvasDesignStore.getters.workspaceUsers;
    },
    filterKeyword() {
      return this.formData && this.formData.name;
    },
    suggestions() {
      const suggestions = [
        { name: "Open", color: "#2e7d32" },
        { name: "In Progress", color: "#0d47a1" },
        { name: "Done", color: "#00838f" }
      ];
      if (this.states && this.states.length) {
        return suggestions.filter((x) =>
          this.states.findIndex((y) => 
            (y.name && y.name.toLowerCase().trim() === x.name.toLowerCase().trim())
          ) === -1
        );
      }
      return suggestions;
    },
    isCreateOption() {
      return this.designMode && this.filterKeyword && !this.$v.$invalid;
    },
    isSuggestState() {
      return this.selectedState.name &&
        this.suggestions.some((x) => x.name && x.name.toLowerCase().trim() == this.selectedState.name.toLowerCase().trim());
    },
    getOptions() {
      let options = [];
      let states = Array.isArray(this.states) ? this.states : [];
      let suggestions = this.designMode && Array.isArray(this.suggestions) ? this.suggestions : [];
      if (this.filterKeyword) {
        if (this.designMode && !this.$v.$invalid) {
          options = [
            { type: 'create', value: this.formData }
          ]
        }
        if (states.length) {
          states = states.filter((x) =>
            x.name && x.name.toLowerCase().trim().indexOf(this.filterKeyword.toLowerCase().trim()) > -1
          );
        }
        if (suggestions.length) {
          suggestions = suggestions.filter((x) =>
            x.name && x.name.toLowerCase().trim().indexOf(this.filterKeyword.toLowerCase().trim()) > -1
          );
        }
      }
      if (states.length) {
        options = [
          ...options,
          { type: 'label', value: 'States' },
          ...states.map((x) => ({ type: 'select', value: x }))
        ];
      }
      if (suggestions.length) {
        options = [
          ...options,
          { type: 'label', value: 'Suggestions' },
          ...suggestions.map((x) => ({ type: 'suggestion', value: x }))
        ];
      }
      if (!this.disableBlankOption && this.selectedState && this.selectedState.id) {
        options = [...options, { type: 'clear' }];
      }
      this.selectedIndex = options.indexOf(options.find(x => x.type !== 'label'));
      return options;
    }
  },
  created() {
    this.referenceId = `inistate-ref-${this._uid}`;
  },
  mounted() {
    if (this.designMode && this.initialSetSuggestState && this.suggestions && this.suggestions[0]) {
      this.$emit("update:selectedState", canvasDesign.initState({...this.suggestions[0], initial: true}));
    }
  }
}
</script>

<style lang="scss">
.states-design-dropdown-menu {
  .dropdown-option {
    display: flex;
    justify-content: space-between;
  }
  .dropdown-option-action {
    display: flex;
    align-items: center;
    > * {
      margin-left: 0.25rem;
    }
  }
  .dropdown-option-state-manage-toggle {
    height: 1.25rem;
    width: 1.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 0.25rem;
    color: var(--fts-default);
    &:hover {
      background-color: rgba(var(--fts-default-rgb), 0.2);
    }
  }
}
.fts-states-design-dropdown {
  .states-design-toggle {
    .fts-state {
      display: block;
      cursor: inherit;
    }
  }
  .states-design-toggle-suggest-state {
    .fts-state {
      opacity: 0.65;
    }
  }
  .states-design-toggle-label {
    padding-left: 0.375rem;
    padding-right: 0.375rem;
    border-radius: 0.25rem;
    font-style: italic;
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: var(--fts-default);
    background-color: rgba(var(--fts-gray-100-rgb), 0.75);
  }
}

.fts-workflow-bar .fts-states-design-dropdown {
  .states-design-toggle-suggest-state {
    padding: 0.175rem 0.175rem;
    border: 2px dashed var(--fts-gray-300);
    border-radius: 0.25rem;
  }
  .states-design-toggle-label {
    padding: 0.175rem 0.375rem;
    border: 2px dashed var(--fts-gray-300);
    background-color: unset;
  }
}
</style>